import { useAuth } from './useAuth'
import { useState, useEffect } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../utils/api'

interface Permission {
  viewOnly: boolean
  editTasks: boolean
  editDeals: boolean
  editCustomers: boolean
  manageSms: boolean
  viewCustomers: boolean
  viewDeals: boolean
  viewTasks: boolean
  viewReports: boolean
  viewFinancials: boolean
  viewCallLogs: boolean
  exportData: boolean
  manageTeam: boolean
  manageSettings: boolean
  managePipeline: boolean
  manageProperties: boolean
  manageWorkflows: boolean
  manageIntegrations: boolean
  manageRoles: boolean
  viewAuditLogs: boolean
  viewTeamAnalytics: boolean
}

interface UsePermissionsReturn {
  permissions: Permission | null
  loading: boolean
  error: string | null
  hasPermission: (permission: keyof Permission) => boolean
  canEditTasks: () => boolean
  canEditDeals: () => boolean
  canEditCustomers: () => boolean
  canManageSms: () => boolean
  isViewOnly: () => boolean
}

/**
 * Hook for managing user permissions
 * For brokers: returns full permissions
 * For collaborators: fetches and returns assigned permissions
 * For admins: returns full permissions
 */
export function usePermissions(): UsePermissionsReturn {
  const { user, isBroker, isAdmin, isCollaborator } = useAuth()
  const [permissions, setPermissions] = useState<Permission | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user) {
        setPermissions(null)
        setLoading(false)
        return
      }

      // Admins and brokers have full permissions
      if (isAdmin || isBroker) {
        setPermissions({
          viewOnly: false,
          editTasks: true,
          editDeals: true,
          editCustomers: true,
          manageSms: true,
          viewCustomers: true,
          viewDeals: true,
          viewTasks: true,
          viewReports: true,
          viewFinancials: true,
          viewCallLogs: true,
          exportData: true,
          manageTeam: true,
          manageSettings: true,
          managePipeline: true,
          manageProperties: true,
          manageWorkflows: true,
          manageIntegrations: true,
          manageRoles: true,
          viewAuditLogs: true,
          viewTeamAnalytics: true
        })
        setLoading(false)
        return
      }

      // Collaborators need to fetch their permissions
      if (isCollaborator) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/collaborators/broker-info`)
          const collaboratorPermissions = response.data.collaboration.permissions
          
          setPermissions(collaboratorPermissions)
          setError(null)
        } catch (err: any) {
          console.error('Error fetching collaborator permissions:', err)
          setError('Failed to load permissions')
          // Default to view-only if error
          setPermissions({
            viewOnly: true,
            editTasks: false,
            editDeals: false,
            editCustomers: false,
            manageSms: false,
            viewCustomers: true,
            viewDeals: true,
            viewTasks: true,
            viewReports: false,
            viewFinancials: false,
            viewCallLogs: false,
            exportData: false,
            manageTeam: false,
            manageSettings: false,
            managePipeline: false,
            manageProperties: false,
            manageWorkflows: false,
            manageIntegrations: false,
            manageRoles: false,
            viewAuditLogs: false,
            viewTeamAnalytics: false
          })
        }
      }

      setLoading(false)
    }

    fetchPermissions()
  }, [user, isAdmin, isBroker, isCollaborator])

  const hasPermission = (permission: keyof Permission): boolean => {
    if (!permissions) return false
    if (isAdmin || isBroker) return true // Admins and brokers have all permissions
    return permissions[permission]
  }

  const canEditTasks = (): boolean => {
    return hasPermission('editTasks')
  }

  const canEditDeals = (): boolean => {
    return hasPermission('editDeals')
  }

  const canEditCustomers = (): boolean => {
    return hasPermission('editCustomers')
  }

  const canManageSms = (): boolean => {
    return hasPermission('manageSms')
  }

  const isViewOnly = (): boolean => {
    if (isAdmin || isBroker) return false
    return permissions?.viewOnly === true && 
           !permissions?.editTasks && 
           !permissions?.editDeals && 
           !permissions?.editCustomers && 
           !permissions?.manageSms
  }

  return {
    permissions,
    loading,
    error,
    hasPermission,
    canEditTasks,
    canEditDeals,
    canEditCustomers,
    canManageSms,
    isViewOnly
  }
}